import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Cartao,
  EstadoCarregando,
  EstadoErro,
  EstadoVazio,
  Selo,
} from '../componentes/ui';
import { requisicaoAutenticada } from '../servicos/http';
import { formatarCpf } from '../servicos/formato';
import { Pagina } from './Carteira';
import { CadastroDeColaborador } from './cliente/FormulariosDeColaborador';
import type { PessoaResumo } from './Pessoas';
import { formatarTelefone } from './vinculos-trabalhistas';

/**
 * RN-311 — o cadastro de uma pessoa: contato, acesso e empresas.
 *
 * ## A porta de quem está só na agenda
 *
 * Pessoa cadastrada sem empresa não tem conta nem acesso — é um contato.
 * "Vincular a uma empresa" é o que muda isso: abre o mesmo cadastro de
 * colaborador, com os dados pessoais já prontos, e a conta nasce junto com o
 * vínculo. Quem já tem empresa usa o mesmo botão para entrar em mais uma.
 *
 * ## Sem CPF não vincula
 *
 * O cadastro de colaborador acha a pessoa pelo CPF. Os cadastros que vieram da
 * migração sem CPF (marcador `x…`) não teriam como ser achados — o vínculo
 * criaria outra pessoa. O botão fica travado e diz por quê.
 */
export function PessoaDetalhe(): JSX.Element {
  const { personId } = useParams<{ personId: string }>();
  const [vinculando, setVinculando] = useState(false);

  // Sob `['pessoas', …]` de propósito: o cadastro e a exclusão já invalidam
  // essa chave, e o detalhe se atualiza junto com a lista.
  const consulta = useQuery({
    queryKey: ['pessoas', 'detalhe', personId],
    queryFn: () => requisicaoAutenticada<PessoaResumo>(`/business/pessoas/${personId ?? ''}`),
    enabled: personId !== undefined,
  });

  if (consulta.isLoading) return <EstadoCarregando contexto="a pessoa" />;
  if (consulta.isError) {
    return (
      <EstadoErro
        titulo="Não foi possível carregar a pessoa"
        mensagem={consulta.error.message}
        aoTentarNovamente={() => void consulta.refetch()}
      />
    );
  }

  const p = consulta.data;
  if (p === undefined) return <EstadoVazio titulo="Pessoa não encontrada" />;

  const podeVincular = p.ativo && p.cpf !== null;

  return (
    <Pagina
      titulo={p.nome}
      descricao={p.cpf === null ? 'Sem CPF' : `CPF ${formatarCpf(p.cpf)}`}
      acao={
        <Link
          to="/pessoas"
          className="text-sm text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
        >
          ← Voltar para pessoas
        </Link>
      }
    >
      {!p.ativo && (
        <Aviso tom="atencao" titulo="Pessoa excluída">
          Este cadastro está inativo: a pessoa não entra na plataforma e os vínculos foram
          encerrados. O histórico continua registrado.
        </Aviso>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Cartao como="section" titulo="Contato">
          <dl className="grid gap-4 sm:grid-cols-2">
            <Dado rotulo="E-mail" valor={p.emailAddress} />
            <Dado
              rotulo="Telefone"
              valor={p.telefone === null ? 'Não informado' : formatarTelefone(p.telefone)}
            />
          </dl>
        </Cartao>

        <Cartao como="section" titulo="Acesso ao sistema">
          {p.temAcesso ? (
            <div className="flex flex-col gap-2">
              <Selo tom="positivo">Ativo</Selo>
              <p className="text-sm text-conteudo-secundario">
                {p.ultimoAcesso === null
                  ? 'Ainda não entrou na plataforma.'
                  : `Último acesso em ${new Date(p.ultimoAcesso).toLocaleDateString('pt-BR')}.`}
              </p>
            </div>
          ) : (
            <p className="text-sm text-conteudo-secundario">
              Sem acesso. A conta é criada quando a pessoa é vinculada a uma empresa — até lá, o
              cadastro serve só como contato.
            </p>
          )}
        </Cartao>
      </div>

      <Cartao
        como="section"
        titulo="Empresas"
        subtitulo={
          p.cpf === null
            ? 'Cadastro sem CPF, vindo da migração: o vínculo exige CPF para achar a pessoa.'
            : undefined
        }
        acao={
          p.ativo && (
            <Botao tamanho="pequeno" disabled={!podeVincular} onClick={() => setVinculando(true)}>
              Vincular a uma empresa
            </Botao>
          )
        }
      >
        {p.vinculos.length === 0 ? (
          <p className="text-sm text-conteudo-secundario">
            Nenhuma empresa. A pessoa está só na agenda.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-borda/60">
            {p.vinculos.map((v) => (
              <li
                key={`${v.businessId}-${v.papel}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
              >
                <span className="flex flex-col">
                  <Link
                    to={`/empresas/${v.businessId}`}
                    className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
                  >
                    {v.razaoSocial}
                  </Link>
                  <span className="text-xs text-conteudo-sutil">
                    {v.papel}
                    {v.ocupacao === null ? '' : ` · ${v.ocupacao}`}
                  </span>
                </span>
                <Selo tom={v.ativo ? 'positivo' : 'neutro'}>{v.ativo ? 'Ativo' : 'Encerrado'}</Selo>
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      {podeVincular && (
        <CadastroDeColaborador
          pessoa={p}
          aberto={vinculando}
          aoFechar={() => setVinculando(false)}
        />
      )}
    </Pagina>
  );
}

function Dado(props: { readonly rotulo: string; readonly valor: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-conteudo-sutil">{props.rotulo}</dt>
      <dd className="text-sm text-conteudo-primario">{props.valor}</dd>
    </div>
  );
}
